import React, { useState, useEffect, useRef } from "react";
import { Wrapper } from "./style";
import { useSelector, useDispatch } from "react-redux";
import platform from "platform";
import { useRouter } from "next/router";
import useMediaQuery from "@mui/material/useMediaQuery";
import lodashDebounce from "lodash.debounce";

// Components
import CenterBio from "./CenterBio";
import Services from "./Services";
import ContactInfo from "./ContactInfo";
import WeeklySchedule from "./WeeklySchedule";
import MedicalTeam from "./MedicalTeam";
import CustomFAQ from "components/common/CustomFAQ";
import Button from "components/common/Button";

// Libs
import Translation from "libs/Translation";
import ModalManager from "libs/ModalManager";

// Action
import { getAPI } from "redux/core/actions";

const CenterProfile = ({ center, webConfig, pageFAQ }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const ref = useRef(null);
  const mobileView = useMediaQuery("(max-width:600px)");
  const { appPromptClose } = useSelector((state) => state.core);

  const [isMobile, setIsMobile] = useState(false);
  const [sidebarTop, setSidebarTop] = useState();
  const [height, setHeight] = useState();
  const [secondHeight, setSecondHeight] = useState();
  const [medicalTeam, setMedicalTeam] = useState();
  const [loading, setLoading] = useState();

  // Shared state for word-by-word filtering
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setIsMobile(["Android", "iOS"].includes(platform.os?.family));
  }, []);

  useEffect(() => {
    setLoading(true);
    dispatch(
      getAPI(
        "HPMedicalTeam",
        { health_provider_profile_id: center?._id },
        "nestApi"
      )
    )
      .then((res) => {
        setMedicalTeam(res?.team);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [center?._id, dispatch]);

  const handleShowMedicalTeam = (initialSearch = "") => {
    const activeSearch = initialSearch || searchTerm;

    ModalManager.show({
      content: (
        <Wrapper
          webConfig={webConfig}
          appPromptClose={appPromptClose}
          isMobile={isMobile}
          inModal={true}
        >
          <MedicalTeam
            center={center}
            webConfig={webConfig}
            mobileView={mobileView}
            inModal={true}
            loading={loading}
            medicalTeamList={medicalTeam}
            externalSearchTerm={activeSearch}
            onSearchChange={setSearchTerm}
          />
        </Wrapper>
      ),
      title: "",
      maxWidth: "sm",
      background: "#F5F5F7",
      fullScreenMobile: true,
      hideCloseButton: false,
      closable: true,
    });
  };

  const handleServiceClick = (serviceTitle) => {
    setSearchTerm(serviceTitle);
    if (mobileView) {
      // 🚀 Flow update: Open modal immediately on mobile click
      handleShowMedicalTeam(serviceTitle);
    } else {
      // Smooth scroll to sidebar on desktop
      ref.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Sticky Logic
  const debouncedSetHeight = lodashDebounce(() => {
    if (ref.current) setSecondHeight(ref.current.clientHeight);
  }, 1000);

  useEffect(() => {
    if (medicalTeam?.length <= 2) debouncedSetHeight();
  }, [router, medicalTeam, debouncedSetHeight]);

  useEffect(() => {
    if (medicalTeam?.length <= 2) {
      const secColumn = document
        .querySelector(".second-column")
        ?.getBoundingClientRect();
      const column = document
        .querySelector(".first-column")
        ?.getBoundingClientRect();
      if (column && secColumn) {
        setHeight(column.height);
        setSidebarTop(secColumn.top);
      }
    }
  }, [router, medicalTeam]);

  useEffect(() => {
    if (medicalTeam?.length <= 2) {
      if (!sidebarTop || !secondHeight) return;
      window.addEventListener("scroll", isSticky);
      return () => window.removeEventListener("scroll", isSticky);
    }
  }, [sidebarTop, secondHeight, medicalTeam]);

  const isSticky = () => {
    if (medicalTeam?.length <= 2) {
      const secColumn = document.querySelector(".second-column");
      if (!secColumn) return;
      const scrollTop = window.scrollY;
      if (scrollTop >= 60) {
        if (scrollTop - 60 < height - secondHeight) {
          secColumn.classList.add("is-sticky");
          secColumn.classList.remove("is-fixed");
        } else {
          secColumn.classList.remove("is-sticky");
          secColumn.classList.add("is-fixed");
        }
      } else {
        secColumn.classList.remove("is-sticky");
        secColumn.classList.remove("is-fixed");
      }
    }
  };

  return (
    <Wrapper
      webConfig={webConfig}
      appPromptClose={appPromptClose}
      isMobile={isMobile}
    >
      <div className="flex-column-container">
        <div className="first-column">
          <CenterBio
            center={center}
            webConfig={webConfig}
            mobileView={mobileView}
          />
          {(center?.Services?.length || center?.Facilities?.length) && (
            <Services
              center={center}
              mobileView={mobileView}
              onServiceClick={handleServiceClick}
            />
          )}
          <ContactInfo
            center={center}
            webConfig={webConfig}
            mobileView={mobileView}
          />
          {center?.WeeklySchedule?.length && (
            <WeeklySchedule
              center={center}
              webConfig={webConfig}
              mobileView={mobileView}
            />
          )}
          {(center?.FAQ?.length || pageFAQ.length) && (
            <CustomFAQ faq={[...(pageFAQ || []), ...(center?.FAQ || [])]} />
          )}
        </div>
        <div className="second-column" ref={ref}>
          <MedicalTeam
            center={center}
            webConfig={webConfig}
            mobileView={mobileView}
            medicalTeamList={medicalTeam}
            loading={loading}
            externalSearchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </div>
      </div>
      <div className="fixed-button" onClick={() => handleShowMedicalTeam()}>
        <Button className="button">
          {Translation.t("label.view-free-times")}
        </Button>
      </div>
    </Wrapper>
  );
};

export default CenterProfile;
